import React from "react";
import { currencySymbol } from "shared/CurrencySymbol";
import { getAmountDue } from "../bookingStatus";
import { useReceiptDownload } from "../useReceiptDownload";

interface Props {
  booking: any;
  isHost: boolean;
  onViewInvoice?: () => void;
}

/** Confirmed bookings: what was paid, receipt download, invoice number. Same receipt helper as the old page. */
const PaymentSummary: React.FC<Props> = ({ booking: b, isHost, onViewInvoice }) => {
  const { download: onDownload, downloading, error, canDownload } = useReceiptDownload(b);
  const amount = `${currencySymbol()}${getAmountDue(b).toFixed(2)}`;
  const invoiceNo = b?.invoiceNo ?? b?.invoice?.id;

  return (
    <section
      aria-label="Payment"
      className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 sm:p-7"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 text-[13px] text-neutral-500 dark:text-neutral-400">{isHost ? "Guest paid" : "Total paid"}</p>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-4xl font-semibold leading-none text-neutral-900 dark:text-white">{amount}</span>
            <span className="rounded-full bg-green-100 dark:bg-green-900/40 px-3 py-1 text-[13px] font-semibold text-green-800 dark:text-green-300">
              Paid
            </span>
          </div>
        </div>

        {canDownload && (
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            className="inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-[#C2410C] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#9A3412] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
            </svg>
            {downloading ? "Generating PDF..." : "Download receipt"}
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}

      {invoiceNo != null && invoiceNo !== "" && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-neutral-100 dark:border-neutral-800 pt-3 text-sm">
          <span className="text-neutral-500 dark:text-neutral-400">
            Invoice <strong className="font-semibold text-neutral-900 dark:text-neutral-100">#{String(invoiceNo)}</strong>
          </span>
          {onViewInvoice && (
            <button
              type="button"
              onClick={onViewInvoice}
              className="min-h-[44px] font-medium text-[#C2410C] dark:text-[#FF8A6E] hover:underline"
            >
              View full invoice
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default PaymentSummary;
